/**
 * The **Arena elite/boss warning banner + level-up draft veil + hurt vignette**
 * (Arena Slice F, PRD #334, ADR-0178; veil reworked in ADR-0218; hurt vignette
 * added in ADR-0224): transient screen chrome for the run's spike moments. The
 * banner announces an elite or boss entrance top-centre (under the run HUD)
 * and self-dismisses after {@link ARENA_WARNING_TTL_MS}; the veil darkens the
 * whole gameplay window behind the level-up draft - it fades in as the draft
 * opens (the modal-focus dim: the server holds the run paused, the field dims,
 * the choice card is the one lit thing) and fades back out when the pick
 * lands. The veil sits below the draft/results modals and the warning banner
 * in the z-order, above the canvas and the run HUD chrome.
 *
 * The caller drives the veil as a per-frame *target* ({@link setVeil}, fed
 * from the draft overlay's open state in the render loop - last write wins),
 * and {@link sync} eases the opacity toward it on the injected clock, so the
 * fade needs no timers and a mid-fade flip (a banked multi-level draft chain
 * closing and instantly reopening) just retargets smoothly.
 *
 * The **hurt vignette** ({@link hurt}, hit-feedback PRD #430 Slice C) is the
 * third machine: a bite on the runner pulses `#arena-hurt` - an inset red
 * radial-gradient ring, never a full-screen fill, so damage stings without
 * blinding - at a peak opacity scaled by the hit's `damage / maxHp` fraction
 * (floored at {@link ARENA_HURT_MIN_OPACITY} so a graze still registers,
 * capped at {@link ARENA_HURT_MAX_OPACITY}), fading linearly to nothing over
 * {@link ARENA_HURT_FADE_MS} under the same `sync()` ageing. Last write wins:
 * a fresh bite mid-fade restarts the pulse at its own peak.
 *
 * While the runner is under the low-HP threshold ({@link setDanger}, Slice D /
 * ADR-0227) the same vignette also **breathes**: a slow sinusoidal swell
 * between {@link ARENA_DANGER_BREATH_LOW} and {@link ARENA_DANGER_BREATH_HIGH}
 * over {@link ARENA_DANGER_BREATH_PERIOD_MS} - deliberately quieter than any
 * sting's floor, a held dread rather than a hit. Each `sync()` renders the
 * MAX of the sting's fade and the breathing baseline, so a one-shot `hurt()`
 * always wins while it outshines the breath and hands back to it as it decays
 * (the vignette never blinks off mid-danger). `setDanger` is level-driven
 * (the setVeil pattern - the caller re-asserts the current state, idempotent),
 * so every release path (heal, run end, death, reset) is one `false`.
 *
 * Client-chrome discipline (the storm board / arena HUD rules): plain-DOM
 * sibling overlays of the canvas (markup/CSS in `index.html`), `textContent`
 * only, `pointer-events: none`, missing markup tolerated, a `reset()` reconnect
 * guard (PR #243 - a mid-run disconnect forfeits with no push that would ever
 * hide these), and ageing driven from the render loop via `sync()` against an
 * injected clock so tests are deterministic.
 */

/** How long the elite/boss banner stays up (ms). */
export const ARENA_WARNING_TTL_MS = 2600;

/** How long the draft veil takes to fade fully in as the draft opens (ms). */
export const ARENA_VEIL_FADE_IN_MS = 220;

/** How long the veil takes to fade back out after the pick (ms) - a touch
 * slower than the fade-in, so the field re-lights rather than snapping. */
export const ARENA_VEIL_FADE_OUT_MS = 320;

/** The veil's full darkness: a heavy dim, never a blackout - the frozen
 * battlefield stays readable behind the draft card. */
export const ARENA_VEIL_MAX_OPACITY = 0.6;

/** How long a hurt pulse takes to fade back to nothing (ms). */
export const ARENA_HURT_FADE_MS = 300;

/** The hurt pulse's floor: even a graze must visibly sting. */
export const ARENA_HURT_MIN_OPACITY = 0.15;

/** The hurt pulse's cap: a massive bite stings hard but never blinds. */
export const ARENA_HURT_MAX_OPACITY = 0.5;

/** The danger breathing's trough opacity (Slice D): a low ember, never a sting. */
export const ARENA_DANGER_BREATH_LOW = 0.06;

/** The danger breathing's crest opacity: still under the sting floor's read. */
export const ARENA_DANGER_BREATH_HIGH = 0.18;

/** One full breath (trough -> crest -> trough), ms: slow, like held breath. */
export const ARENA_DANGER_BREATH_PERIOD_MS = 2400;

/** The elite entrance banner copy - fallback when the entrant's name is unknown (the live banner names the creature). */
export const ARENA_ELITE_WARNING_TEXT = 'An elite tears into the arena!';

/** The boss entrance banner copy - fallback when the villain's name is unknown (the live banner names the boss, e.g. "LORD BLACKTHORN ENTERS THE ARENA"). */
export const ARENA_BOSS_WARNING_TEXT = 'LORD BLACKTHORN ENTERS THE ARENA';

/** The two banner kinds, styled distinctly via `data-kind` in the CSS. */
export type ArenaWarningKind = 'elite' | 'boss';

/** Options for {@link initArenaWarning}; injected by tests, defaulted live. */
export interface ArenaWarningOptions {
  doc?: Document;
  /** Wall-clock source for the TTLs/fades, defaulting to `performance.now()`. */
  now?: () => number;
}

/** The handle `main.ts` drives the warning chrome through. */
export interface ArenaWarningHandle {
  /** Show the banner with `text`, restyled by `kind`; last write wins. */
  warn(text: string, kind: ArenaWarningKind): void;
  /**
   * Set the draft veil's target: `true` fades the darkness in, `false` fades it
   * out. Called every render frame with the draft overlay's open state (cheap -
   * it only stores the target; `sync` does the easing).
   */
  setVeil(on: boolean): void;
  /**
   * Pulse the red hurt vignette for a bite worth `fraction` of the runner's max
   * HP: peak opacity = the fraction floored/capped to
   * [{@link ARENA_HURT_MIN_OPACITY}, {@link ARENA_HURT_MAX_OPACITY}], fading
   * out over {@link ARENA_HURT_FADE_MS} via `sync()`. Last write wins; a
   * non-positive or non-finite fraction is a defensive no-op.
   */
  hurt(fraction: number): void;
  /**
   * Set the low-HP danger breathing's level (Slice D, ADR-0227): `true` holds
   * the hurt vignette at a slow breathing baseline, `false` releases it.
   * Level-driven and idempotent (re-asserted from the status handler); a live
   * `hurt()` sting still wins over the baseline while it is brighter.
   */
  setDanger(on: boolean): void;
  /** Age the banner + ease the veil against the clock; called each render frame
   * (cheap no-op when idle). */
  sync(): void;
  /** Hide both immediately - the every-Welcome reconnect guard. */
  reset(): void;
}

/**
 * Wire the `#arena-warning` banner, `#arena-veil` overlay, and `#arena-hurt`
 * vignette (all declared hidden in `index.html`). Missing markup degrades
 * every call to a no-op.
 */
export function initArenaWarning(opts: ArenaWarningOptions = {}): ArenaWarningHandle {
  const doc = opts.doc ?? document;
  const now = opts.now ?? (() => performance.now());
  const banner = doc.getElementById('arena-warning');
  const veilEl = doc.getElementById('arena-veil');
  const hurtEl = doc.getElementById('arena-hurt');

  /** When the live banner expires; 0 = no banner up. */
  let bannerDeadline = 0;
  /** The veil's target state (the draft overlay's open flag, per frame). */
  let veilOn = false;
  /** The veil's current eased opacity, [0, ARENA_VEIL_MAX_OPACITY]. */
  let veilOpacity = 0;
  /** The last sync instant, for the fade's dt; null until the first sync. */
  let veilLastAt: number | null = null;
  /** The live hurt pulse's peak opacity; 0 = no pulse up. */
  let hurtPeak = 0;
  /** When the live hurt pulse started (its fade anchor). */
  let hurtStartedAt = 0;
  /** Whether the low-HP danger breathing is engaged (Slice D). */
  let dangerOn = false;
  /** When the breathing engaged (its phase anchor - every entry starts at the trough). */
  let dangerSince = 0;

  const clearHurt = (): void => {
    hurtPeak = 0;
    if (hurtEl && !dangerOn) {
      hurtEl.hidden = true;
      hurtEl.style.opacity = '0';
    }
  };

  /** The breathing baseline's opacity at instant `at`; 0 while not in danger. */
  const breathOpacity = (at: number): number => {
    if (!dangerOn) return 0;
    const phase = ((at - dangerSince) / ARENA_DANGER_BREATH_PERIOD_MS) * 2 * Math.PI;
    return (
      ARENA_DANGER_BREATH_LOW +
      (ARENA_DANGER_BREATH_HIGH - ARENA_DANGER_BREATH_LOW) * 0.5 * (1 - Math.cos(phase))
    );
  };

  return {
    warn(text, kind) {
      if (!banner) return;
      banner.textContent = text;
      banner.dataset.kind = kind;
      banner.hidden = false;
      bannerDeadline = now() + ARENA_WARNING_TTL_MS;
    },
    setVeil(on) {
      veilOn = on;
    },
    hurt(fraction) {
      if (!hurtEl) return;
      if (!Number.isFinite(fraction) || fraction <= 0) return;
      hurtPeak = Math.min(ARENA_HURT_MAX_OPACITY, Math.max(ARENA_HURT_MIN_OPACITY, fraction));
      hurtStartedAt = now();
      hurtEl.hidden = false;
      hurtEl.style.opacity = String(hurtPeak);
    },
    setDanger(on) {
      if (on === dangerOn) return;
      dangerOn = on;
      if (on) {
        dangerSince = now();
        return;
      }
      // Released (heal / run end / death): drop the baseline now, but never
      // swallow a live sting - its own fade still owns the element.
      if (hurtEl && hurtPeak <= 0) {
        hurtEl.hidden = true;
        hurtEl.style.opacity = '0';
      }
    },
    sync() {
      const at = now();
      if (banner && bannerDeadline > 0 && at > bannerDeadline) {
        banner.hidden = true;
        bannerDeadline = 0;
      }
      if (veilEl) {
        const dt = veilLastAt === null ? 0 : Math.max(0, at - veilLastAt);
        veilLastAt = at;
        const target = veilOn ? ARENA_VEIL_MAX_OPACITY : 0;
        if (veilOpacity !== target) {
          // Linear ease toward the target at the fade's own rate; frame-rate
          // independent (dt-scaled), timer-free, and mid-fade retargeting just
          // reverses direction from wherever the opacity currently is.
          const rate = veilOn
            ? ARENA_VEIL_MAX_OPACITY / ARENA_VEIL_FADE_IN_MS
            : ARENA_VEIL_MAX_OPACITY / ARENA_VEIL_FADE_OUT_MS;
          veilOpacity = veilOn
            ? Math.min(target, veilOpacity + rate * dt)
            : Math.max(0, veilOpacity - rate * dt);
          if (veilOpacity > 0) {
            veilEl.hidden = false;
            veilEl.style.opacity = String(veilOpacity);
          } else {
            veilEl.hidden = true;
            veilEl.style.opacity = '0';
          }
        }
      }
      if (hurtEl && (hurtPeak > 0 || dangerOn)) {
        // The sting's linear fade from its own peak (a fresh hurt() re-anchors
        // the start instant, so last-write-wins restarts fall out for free)...
        let sting = 0;
        if (hurtPeak > 0) {
          const elapsed = Math.max(0, at - hurtStartedAt);
          if (elapsed >= ARENA_HURT_FADE_MS) hurtPeak = 0;
          else sting = hurtPeak * (1 - elapsed / ARENA_HURT_FADE_MS);
        }
        // ...vs the danger breathing baseline (Slice D): the brighter wins, so
        // a bite always reads over the breath and decays back into it.
        const opacity = Math.max(sting, breathOpacity(at));
        if (opacity > 0) {
          hurtEl.hidden = false;
          hurtEl.style.opacity = String(opacity);
        } else {
          hurtEl.hidden = true;
          hurtEl.style.opacity = '0';
        }
      }
    },
    reset() {
      if (banner) banner.hidden = true;
      bannerDeadline = 0;
      veilOn = false;
      veilOpacity = 0;
      veilLastAt = null;
      if (veilEl) {
        veilEl.hidden = true;
        veilEl.style.opacity = '0';
      }
      dangerOn = false;
      clearHurt();
    },
  };
}
